import { useEffect, useState } from "react";
import { useListOpenaiConversations } from "@workspace/api-client-react";

export type RetentionDays = 30 | 90 | 180 | null;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function useRetentionDays(): { retentionDays: RetentionDays; loading: boolean } {
  const [retentionDays, setRetentionDays] = useState<RetentionDays>(90);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/user/preferences")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { retentionDays: RetentionDays } | null) => {
        if (cancelled) return;
        if (data && (data.retentionDays === 30 || data.retentionDays === 90 || data.retentionDays === 180 || data.retentionDays === null)) {
          setRetentionDays(data.retentionDays);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { retentionDays, loading };
}

export interface OldestChatCountdown {
  /** Absolute date when the oldest conversation will be deleted. */
  deletionDate: Date;
  /** Whole days until deletion (can be negative/zero if overdue/today). */
  daysUntil: number;
  /** Friendly relative phrase, e.g. "in 12 days". */
  relativePhrase: string;
  /** Full absolute date string (long UK format). */
  absoluteLabel: string;
  /** ISO string of the oldest conversation's createdAt. */
  oldestCreatedAt: string;
}

export function computeOldestChatCountdown(
  oldestCreatedAtIso: string,
  retentionDays: number,
  now: Date = new Date(),
): OldestChatCountdown {
  const created = new Date(oldestCreatedAtIso);
  const deletionDate = new Date(created.getTime() + retentionDays * MS_PER_DAY);
  const diffMs = deletionDate.getTime() - now.getTime();
  const daysUntil = Math.ceil(diffMs / MS_PER_DAY);

  let relativePhrase: string;
  if (diffMs <= 0) {
    relativePhrase = "any moment now";
  } else if (daysUntil === 1) {
    relativePhrase = "tomorrow";
  } else {
    relativePhrase = `in ${daysUntil} days`;
  }

  const absoluteLabel = deletionDate.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return {
    deletionDate,
    daysUntil,
    relativePhrase,
    absoluteLabel,
    oldestCreatedAt: oldestCreatedAtIso,
  };
}

/**
 * Returns countdown info for the oldest conversation, or null if:
 *  - data is still loading
 *  - the user has no conversations
 *  - the user has chosen "Keep until I delete" (retentionDays === null)
 */
export function useOldestChatDeletionCountdown(): OldestChatCountdown | null {
  const { data: convos } = useListOpenaiConversations();
  const { retentionDays, loading } = useRetentionDays();

  if (loading) return null;
  if (retentionDays === null) return null;
  if (!convos || convos.length === 0) return null;

  let oldestIso: string | null = null;
  for (const c of convos) {
    const iso = (c as { createdAt?: string | Date }).createdAt;
    if (!iso) continue;
    const isoStr = typeof iso === "string" ? iso : iso.toISOString();
    if (oldestIso === null || new Date(isoStr).getTime() < new Date(oldestIso).getTime()) {
      oldestIso = isoStr;
    }
  }
  if (!oldestIso) return null;

  return computeOldestChatCountdown(oldestIso, retentionDays);
}
